<template>
  <div class="notice-page">
    <section class="notice-toolbar page-card">
      <div class="notice-toolbar__copy">
        <div class="page-card__title">爬仔团队</div>
        <div class="page-card__desc">
          发布公告、版本更新与投票。小程序首页「爬仔团队」入口和爬仔团队页会同步展示；草稿状态不会出现在小程序里。
        </div>
      </div>
      <div class="notice-toolbar__actions">
        <span class="notice-count">{{ notices.length }} 条消息</span>
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
        <button class="primary-button" type="button" @click="startCreate">新建消息</button>
      </div>
    </section>

    <section class="notice-body">
      <aside class="notice-list page-card">
        <div class="notice-filter">
          <button
            v-for="item in categoryFilters"
            :key="item.value"
            type="button"
            class="filter-chip"
            :class="{ 'filter-chip--active': filterCategory === item.value }"
            @click="filterCategory = item.value"
          >
            {{ item.label }}
          </button>
        </div>

        <div class="notice-list__scroll">
          <div v-if="loading" class="list-state">正在加载消息…</div>
          <div v-else-if="!filteredNotices.length" class="list-state">还没有消息，点右上角新建。</div>
          <template v-else>
            <div
              v-for="item in filteredNotices"
              :key="String(item.id)"
              class="notice-item"
              :class="{ 'notice-item--active': form.id === item.id }"
              @click="selectNotice(item)"
            >
              <div class="notice-item__top">
                <span class="category-badge" :class="categoryClass(item.category)">{{ item.categoryLabel }}</span>
                <span v-if="item.pinned === 1" class="pin-badge">置顶</span>
                <span class="status-badge" :class="item.status === 1 ? 'status-badge--on' : 'status-badge--off'">
                  {{ item.status === 1 ? "已发布" : "草稿" }}
                </span>
              </div>
              <div class="notice-item__title">{{ item.title }}</div>
              <div class="notice-item__meta">
                {{ formatTime(item.publishedAt) }}
                <template v-if="item.category === 'VOTE'">· {{ item.voteTotal || 0 }} 人已投</template>
              </div>
            </div>
          </template>
        </div>
      </aside>

      <article class="notice-editor page-card">
        <header class="editor-header">
          <div>
            <div class="editor-header__eyebrow">{{ form.id ? "正在编辑 #" + form.id : "新建" }}</div>
            <h2>{{ form.id ? (form.title || "未命名消息") : "新建爬仔团队消息" }}</h2>
          </div>
          <div class="editor-header__actions">
            <button v-if="form.id" class="ghost-button" type="button" :disabled="submitting" @click="removeCurrent">
              删除
            </button>
            <button class="primary-button" type="button" :disabled="submitting" @click="save">
              {{ submitting ? "保存中…" : (form.id ? "保存" : "发布") }}
            </button>
          </div>
        </header>

        <div v-if="message.text" class="editor-message" :class="message.type === 'error' ? 'editor-message--error' : 'editor-message--success'">
          {{ message.text }}
        </div>

        <div class="editor-scroll">
          <div class="field-block">
            <label class="field-label">消息类型</label>
            <div class="type-row">
              <button
                v-for="item in categoryOptions"
                :key="item.value"
                type="button"
                class="type-chip"
                :class="{ 'type-chip--active': form.category === item.value }"
                @click="setCategory(item.value)"
              >
                <span class="type-chip__name">{{ item.label }}</span>
                <span class="type-chip__desc">{{ item.desc }}</span>
              </button>
            </div>
          </div>

          <div class="field-block">
            <label class="field-label">标题</label>
            <input v-model.trim="form.title" class="field-input" type="text" maxlength="128" placeholder="例如：v1.6.0 更新说明" />
          </div>

          <div class="field-block">
            <label class="field-label">摘要（列表里显示一行，可不填）</label>
            <input v-model.trim="form.summary" class="field-input" type="text" maxlength="255" placeholder="例如：新增爬仔团队与投票，优化蜕皮记录" />
          </div>

          <div class="field-block">
            <label class="field-label">封面图（可不填）</label>
            <div class="cover-row">
              <div class="cover-preview">
                <img v-if="form.coverImage" :src="resolveAssetUrl(form.coverImage)" alt="" />
                <span v-else>无封面</span>
              </div>
              <div class="cover-actions">
                <label class="upload-button">
                  {{ uploadingCover ? "上传中…" : "上传封面" }}
                  <input type="file" accept="image/*" @change="onCoverSelected" />
                </label>
                <button v-if="form.coverImage" class="ghost-button" type="button" @click="form.coverImage = ''">移除</button>
              </div>
            </div>
          </div>

          <div class="field-block">
            <label class="field-label">正文</label>
            <textarea v-model="form.content" class="field-textarea" rows="8" placeholder="支持换行分段。发布后小程序按段落展示。" />
          </div>

          <div v-if="form.category === 'VOTE'" class="field-block">
            <label class="field-label">投票选项（单选，用户只能投一次，可改投）</label>
            <div class="option-list">
              <div v-for="(option, index) in form.options" :key="'option-' + index" class="option-row">
                <input v-model.trim="option.label" class="field-input option-input" type="text" maxlength="64" :placeholder="'选项 ' + (index + 1)" />
                <span v-if="option.voteCount > 0" class="option-votes">{{ option.voteCount }} 票</span>
                <button class="ghost-button option-remove" type="button" :disabled="form.options.length <= 2" @click="removeOption(index)">删除</button>
              </div>
              <button class="ghost-button option-add" type="button" :disabled="form.options.length >= 8" @click="addOption">＋ 添加选项</button>
            </div>
          </div>

          <div class="field-grid">
            <div class="field-block">
              <label class="field-label">跳转</label>
              <select v-model="form.linkType" class="field-input">
                <option value="NONE">不跳转</option>
                <option value="PAGE">跳转小程序页面</option>
              </select>
            </div>
            <div v-if="form.linkType === 'PAGE'" class="field-block">
              <label class="field-label">页面路径</label>
              <input v-model.trim="form.linkUrl" class="field-input" type="text" placeholder="/pages/square/index" />
            </div>
          </div>

          <div class="field-grid">
            <div class="field-block">
              <label class="field-label">状态</label>
              <select v-model.number="form.status" class="field-input">
                <option :value="1">发布</option>
                <option :value="0">草稿</option>
              </select>
            </div>
            <div class="field-block">
              <label class="field-label">置顶</label>
              <select v-model.number="form.pinned" class="field-input">
                <option :value="0">普通</option>
                <option :value="1">置顶</option>
              </select>
            </div>
          </div>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import { deleteNotice, fetchNoticeDetail, fetchNotices, saveNotice } from "../api/notice"
import { uploadAdminImage } from "../api/user"
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"

function createEmptyOption(label = "") {
  return { optionKey: "", label, voteCount: 0 }
}

function createEmptyForm() {
  return {
    id: null,
    category: "ANNOUNCEMENT",
    title: "",
    summary: "",
    content: "",
    coverImage: "",
    linkType: "NONE",
    linkUrl: "",
    pinned: 0,
    status: 1,
    options: [createEmptyOption(), createEmptyOption()]
  }
}

export default {
  name: "NoticeManageView",
  data() {
    return {
      loading: false,
      submitting: false,
      uploadingCover: false,
      notices: [],
      filterCategory: "",
      form: createEmptyForm(),
      message: { type: "", text: "" },
      categoryFilters: [
        { value: "", label: "全部" },
        { value: "ANNOUNCEMENT", label: "公告" },
        { value: "UPDATE", label: "更新" },
        { value: "VOTE", label: "投票" }
      ],
      categoryOptions: [
        { value: "ANNOUNCEMENT", label: "公告", desc: "官方说明、规则与通知" },
        { value: "UPDATE", label: "更新", desc: "版本更新与功能上线" },
        { value: "VOTE", label: "投票", desc: "收集用户意见，单选" }
      ]
    }
  },
  computed: {
    filteredNotices() {
      if (!this.filterCategory) return this.notices
      return this.notices.filter(item => item.category === this.filterCategory)
    }
  },
  created() {
    this.reload()
  },
  methods: {
    resolveAssetUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    categoryClass(category) {
      if (category === "UPDATE") return "category-badge--update"
      if (category === "VOTE") return "category-badge--vote"
      return "category-badge--announcement"
    },
    formatTime(value) {
      if (!value) return "未发布"
      return String(value).replace("T", " ").slice(0, 16)
    },
    clearMessage() {
      this.message = { type: "", text: "" }
    },
    showError(error) {
      this.message = { type: "error", text: (error && error.message) || "操作失败" }
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchNotices()
        this.notices = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.showError(error)
      } finally {
        this.loading = false
      }
    },
    startCreate() {
      this.form = createEmptyForm()
      this.clearMessage()
    },
    setCategory(category) {
      this.form.category = category
      if (category === "VOTE" && this.form.options.length < 2) {
        this.form.options = [createEmptyOption(), createEmptyOption()]
      }
    },
    addOption() {
      if (this.form.options.length >= 8) return
      this.form.options.push(createEmptyOption())
    },
    removeOption(index) {
      if (this.form.options.length <= 2) return
      this.form.options.splice(index, 1)
    },
    async selectNotice(item) {
      this.clearMessage()
      try {
        const res = await fetchNoticeDetail(item.id)
        const detail = res.data || item
        this.form = {
          id: detail.id,
          category: detail.category || "ANNOUNCEMENT",
          title: detail.title || "",
          summary: detail.summary || "",
          content: detail.content || "",
          coverImage: detail.coverImage || "",
          linkType: detail.linkType || "NONE",
          linkUrl: detail.linkUrl || "",
          pinned: detail.pinned === 1 ? 1 : 0,
          status: detail.status === 0 ? 0 : 1,
          options: Array.isArray(detail.options) && detail.options.length
            ? detail.options.map(option => ({
              optionKey: option.optionKey || "",
              label: option.label || "",
              voteCount: option.voteCount || 0
            }))
            : [createEmptyOption(), createEmptyOption()]
        }
      } catch (error) {
        this.showError(error)
      }
    },
    async onCoverSelected(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) return
      this.uploadingCover = true
      this.clearMessage()
      try {
        const res = await uploadAdminImage(file)
        const data = res.data || {}
        this.form.coverImage = data.url || data.path || ""
      } catch (error) {
        this.showError(error)
      } finally {
        this.uploadingCover = false
        if (event && event.target) event.target.value = ""
      }
    },
    async save() {
      if (this.submitting) return
      if (!this.form.title) {
        this.showError(new Error("请填写标题"))
        return
      }
      if (this.form.category === "VOTE") {
        const labels = this.form.options.map(option => (option.label || "").trim()).filter(Boolean)
        if (labels.length < 2) {
          this.showError(new Error("投票至少需要两个选项"))
          return
        }
      }
      this.submitting = true
      this.clearMessage()
      const payload = {
        id: this.form.id,
        category: this.form.category,
        title: this.form.title,
        summary: this.form.summary,
        content: this.form.content,
        coverImage: this.form.coverImage,
        linkType: this.form.linkType,
        linkUrl: this.form.linkUrl,
        pinned: this.form.pinned,
        status: this.form.status,
        options: this.form.category === "VOTE"
          ? this.form.options
            .filter(option => (option.label || "").trim())
            .map(option => ({ optionKey: option.optionKey, label: option.label.trim() }))
          : []
      }
      try {
        const res = await saveNotice(payload)
        const saved = res.data || {}
        this.message = { type: "success", text: res.message || "已保存" }
        await this.reload()
        if (saved.id) {
          const refreshed = this.notices.find(item => item.id === saved.id)
          if (refreshed) this.selectNotice(refreshed)
          else this.form.id = saved.id
        }
      } catch (error) {
        this.showError(error)
      } finally {
        this.submitting = false
      }
    },
    async removeCurrent() {
      if (!this.form.id) return
      if (!window.confirm(`确认删除「${this.form.title || "这条消息"}」吗？删除后小程序不再展示。`)) return
      this.submitting = true
      this.clearMessage()
      try {
        await deleteNotice(this.form.id)
        this.message = { type: "success", text: "已删除" }
        this.form = createEmptyForm()
        await this.reload()
      } catch (error) {
        this.showError(error)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.notice-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
}

.notice-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.notice-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.notice-count {
  color: #86928c;
  font-size: 13px;
}

.notice-body {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.notice-list {
  display: flex;
  flex-direction: column;
  padding: 14px;
  max-height: calc(100vh - 240px);
}

.notice-filter {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.filter-chip {
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e2eae4;
  background: #fff;
  color: #55605a;
  font-size: 12px;
  cursor: pointer;
}

.filter-chip--active {
  border-color: #17b26a;
  background: #eefaf3;
  color: #0f8b52;
  font-weight: 600;
}

.notice-list__scroll {
  flex: 1;
  overflow-y: auto;
}

.list-state {
  padding: 40px 16px;
  color: #86928c;
  font-size: 13px;
  text-align: center;
}

.notice-item {
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid transparent;
}

.notice-item:hover {
  background: #f7faf8;
}

.notice-item--active {
  background: #eefaf3;
  border-color: #cdeede;
}

.notice-item__top {
  display: flex;
  align-items: center;
  gap: 6px;
}

.category-badge,
.pin-badge,
.status-badge {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
}

.category-badge--announcement { background: #e8f1fd; color: #2f6bb0; }
.category-badge--update { background: #eaf7e6; color: #2f8b3c; }
.category-badge--vote { background: #fdf1e2; color: #b4741c; }
.pin-badge { background: #fdeaea; color: #c0392b; }
.status-badge--on { background: #eef7ee; color: #3d8b4a; }
.status-badge--off { background: #f1f2f4; color: #7c848c; }

.notice-item__title {
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2a24;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-item__meta {
  margin-top: 4px;
  color: #86928c;
  font-size: 12px;
}

.notice-editor {
  display: flex;
  flex-direction: column;
  padding: 18px 20px 20px;
  max-height: calc(100vh - 240px);
}

.editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.editor-header__eyebrow {
  color: #9aa4ae;
  font-size: 12px;
}

.editor-header h2 {
  margin: 4px 0 0;
  font-size: 18px;
  color: #1f2a24;
}

.editor-header__actions {
  display: flex;
  gap: 10px;
}

.editor-message {
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.editor-message--success { background: #eefaf3; color: #0f8b52; }
.editor-message--error { background: #fdecec; color: #c0392b; }

.editor-scroll {
  margin-top: 16px;
  flex: 1;
  overflow-y: auto;
}

.field-block {
  margin-bottom: 16px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #55605a;
  font-size: 13px;
  font-weight: 600;
}

.field-input,
.field-textarea {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #e2eae4;
  border-radius: 8px;
  background: #fbfdfc;
  color: #1f2a24;
  font-size: 13px;
  box-sizing: border-box;
}

.field-textarea {
  line-height: 1.6;
  resize: vertical;
}

.type-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.type-chip {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e2eae4;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.type-chip--active {
  border-color: #17b26a;
  background: #eefaf3;
}

.type-chip__name {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2a24;
}

.type-chip__desc {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #86928c;
}

.cover-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cover-preview {
  width: 132px;
  height: 82px;
  border-radius: 10px;
  background: #f4f7f5;
  border: 1px dashed #d8e3db;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa4ae;
  font-size: 12px;
  overflow: hidden;
  flex: none;
}

.cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-actions {
  display: flex;
  gap: 10px;
}

.upload-button {
  padding: 8px 14px;
  border-radius: 8px;
  background: #1f2a24;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}

.upload-button input {
  display: none;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.option-input {
  flex: 1;
}

.option-votes {
  color: #86928c;
  font-size: 12px;
  white-space: nowrap;
}

.option-remove {
  padding: 6px 12px;
  white-space: nowrap;
}

.option-add {
  align-self: flex-start;
  padding: 6px 14px;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 1100px) {
  .notice-body {
    grid-template-columns: 1fr;
  }
}
</style>
